import { HtmlCardHost } from "@/features/investigations/htmlCards/HtmlCardHost";
import {
  HTML_CARD_EXAMPLE_URL,
  HTML_CARD_INSTRUCTIONS_URL,
  HtmlCardHelp,
} from "@/features/investigations/htmlCards/HtmlCardHelp";
import { InvestigationViewSelector } from "@/features/investigations/components/InvestigationViewSelector";
import { getCardBridgeVersion } from "@/features/investigations/htmlCards/cardSdk";
import { type CardDataGetter, useCardDataSource } from "@/features/investigations/htmlCards/useCardDataSource";
import { fetchSessionContactRecords } from "./ContactsPanel";
import { fetchAllSessionEvents, fetchSessionInteraction } from "./InputTab";
import {
  type InvestigationCardDefinition,
  type InvestigationCardLayout,
  type InvestigationCardType,
  type InvestigationImportResult,
  type InvestigationMetadata,
  type InvestigationRecord,
  type InvestigationTemplate,
  createInvestigation,
  deleteInvestigation,
  getInvestigation,
  listInvestigations,
  updateInvestigation,
} from "@/app/providers/services";
import { useNotification } from "@/common/notifications";
import { buildDecisionReportTree } from "@/components/DecisionReport/buildDecisionReportTree";
import { FileDropZone } from "@/components/FileDropzone/FileDropzone";
import { downloadFileInBrowser } from "@/components/FilePreview/downloadFileInBrowser";
import GraphView from "@/components/GraphView/GraphView";
import { useGraphVisualisation } from "@/components/GraphVisualisation/hooks/useGraphVisualisation";
import RuleGraphProvider from "@/components/GraphVisualisation/providers/RuleGraphProvider";
import RuleGraphNode from "@/components/RuleGraph_FullRelease/components/DebuggerGraphNode";
import { TemplateField } from "@/components/TemplateField";
import { lastPathSegment } from "@/lib/utils";
import GoalMenu from "@/pages/models/release/GoalMenu";
import { AttributeExplanationDetailPanel } from "@/pages/sessions/components/DecisionPanel";
import { type DecisionGraph, processText } from "@imminently/decisively-core";
import Alert from "@imminently/decisively-ui/Alert";
import AttributeSelectedProvider, { useAttributeSelected } from "@imminently/decisively-ui/AttributeSelectedProvider";
import Button from "@imminently/decisively-ui/Button";
import Chip from "@imminently/decisively-ui/Chip";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import GraphDataInlineTree from "@imminently/decisively-ui/GraphDataInlineTree";
import Icon from "@imminently/decisively-ui/Icon";
import InvestigationCard from "@imminently/decisively-ui/InvestigationCard";
import Select, { type SelectOption } from "@imminently/decisively-ui/Select";
import TextInput from "@imminently/decisively-ui/TextInput";
import TimesheetInvestigationCard from "@imminently/decisively-ui/TimesheetInvestigationCard";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import GridLayout, { WidthProvider, type Layout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ROW_HEIGHT = 64;
const GRID_COLUMNS = 12;
const GRID_MARGIN: [number, number] = [6, 6];
const MINIMAP_WIDTH = 180;
const MINIMAP_HEIGHT = 120;
const DashboardGridLayout = WidthProvider(GridLayout);
const MAX_HTML_CARD_BYTES = 1024 * 1024;

const defaultTemplate = (modelId: string, id = "", name = "Untitled investigation"): InvestigationTemplate => ({
  schemaVersion: 1,
  id,
  modelId,
  name,
  canvas: { cards: [] },
});

const getCardTypeInitials = (type: InvestigationCardType | string) => {
  switch (type) {
    case "attribute-list":
      return "AL";
    case "attribute-explanation":
      return "AE";
    case "timesheet-table":
      return "TT";
    case "rule-graph":
      return "RG";
    case "text":
      return "TX";
    case "html":
      return "HT";
    default:
      return "??";
  }
};

const INVESTIGATION_REQUEST_CACHE_TTL_MS = 5000;

type InvestigationRequestCacheEntry<T> = {
  value: T;
  expiresAt: number;
};

const investigationListCache = new Map<string, InvestigationRequestCacheEntry<InvestigationMetadata[]>>();
const investigationListRequests = new Map<string, Promise<InvestigationMetadata[]>>();
const investigationRecordCache = new Map<string, InvestigationRequestCacheEntry<InvestigationRecord>>();
const investigationRecordRequests = new Map<string, Promise<InvestigationRecord>>();

const getCachedInvestigationValue = <T,>(cache: Map<string, InvestigationRequestCacheEntry<T>>, key: string) => {
  const cached = cache.get(key);
  if (!cached) return undefined;
  if (cached.expiresAt < Date.now()) {
    cache.delete(key);
    return undefined;
  }
  return cached.value;
};

const setCachedInvestigationValue = <T,>(
  cache: Map<string, InvestigationRequestCacheEntry<T>>,
  key: string,
  value: T,
) => {
  cache.set(key, {
    value,
    expiresAt: Date.now() + INVESTIGATION_REQUEST_CACHE_TTL_MS,
  });
};

const fetchInvestigationList = (modelId: string, force = false) => {
  if (!force) {
    const cached = getCachedInvestigationValue(investigationListCache, modelId);
    if (cached) return Promise.resolve(cached);

    const inFlight = investigationListRequests.get(modelId);
    if (inFlight) return inFlight;
  }

  const request = listInvestigations
    .submit({ params: { id: modelId } })
    .then((response) => {
      const items = response?.data || [];
      setCachedInvestigationValue(investigationListCache, modelId, items);
      return items;
    })
    .finally(() => investigationListRequests.delete(modelId));

  investigationListRequests.set(modelId, request);
  return request;
};

const getInvestigationRecordCacheKey = (modelId: string, investigationId: string) => `${modelId}:${investigationId}`;

const fetchInvestigationRecord = (modelId: string, investigationId: string, force = false) => {
  const key = getInvestigationRecordCacheKey(modelId, investigationId);
  if (!force) {
    const cached = getCachedInvestigationValue(investigationRecordCache, key);
    if (cached) return Promise.resolve(cached);

    const inFlight = investigationRecordRequests.get(key);
    if (inFlight) return inFlight;
  }

  const request = getInvestigation
    .submit({ params: { id: modelId, investigationId } })
    .then((response) => {
      setCachedInvestigationValue(investigationRecordCache, key, response.data);
      return response.data;
    })
    .finally(() => investigationRecordRequests.delete(key));

  investigationRecordRequests.set(key, request);
  return request;
};

const toStoredGridLayout = (layout: Layout): InvestigationCardLayout => {
  const next: InvestigationCardLayout = {
    x: layout.x,
    y: layout.y,
    w: layout.w,
    h: layout.h,
  };
  for (const key of ["minW", "minH", "maxW", "maxH"] as const) {
    if (layout[key] !== undefined) next[key] = layout[key];
  }
  if (layout.static === true) next.static = true;
  if (layout.isDraggable === false) next.isDraggable = false;
  if (layout.isResizable === false) next.isResizable = false;
  return next;
};

const isSameLayout = (a: InvestigationCardLayout, b: InvestigationCardLayout) => {
  const normalizedA = toStoredGridLayout(a as Layout);
  const normalizedB = toStoredGridLayout(b as Layout);
  return (
    normalizedA.x === normalizedB.x &&
    normalizedA.y === normalizedB.y &&
    normalizedA.w === normalizedB.w &&
    normalizedA.h === normalizedB.h &&
    normalizedA.minW === normalizedB.minW &&
    normalizedA.minH === normalizedB.minH &&
    normalizedA.maxW === normalizedB.maxW &&
    normalizedA.maxH === normalizedB.maxH &&
    normalizedA.static === normalizedB.static &&
    normalizedA.isDraggable === normalizedB.isDraggable &&
    normalizedA.isResizable === normalizedB.isResizable
  );
};

const createCard = (type: InvestigationCardType, index: number): InvestigationCardDefinition => {
  if (type === "html") {
    return {
      id: crypto.randomUUID(),
      type: "html",
      title: "Custom View",
      layout: { x: 0, y: index * 8, w: 12, h: 8, minW: 4, minH: 3 },
      config: { html: "" },
    };
  }
  if (type === "text") {
    return {
      id: crypto.randomUUID(),
      type: "text",
      title: "Notes",
      layout: { x: 0, y: index * 4, w: 5, h: 4, minW: 3, minH: 2 },
      config: { body: "" },
    };
  }
  if (type === "timesheet-table") {
    return {
      id: crypto.randomUUID(),
      type: "timesheet-table",
      title: "Timesheet Summary",
      layout: { x: 0, y: index * 6, w: 10, h: 6, minW: 4, minH: 3 },
      config: {
        positionRelationship: "",
        timeEntryRelationship: "",
        columns: [],
        totals: [],
      },
    };
  }
  if (type === "attribute-explanation") {
    return {
      id: crypto.randomUUID(),
      type: "attribute-explanation",
      title: "Attribute Explanation",
      layout: { x: 0, y: index * 6, w: 8, h: 6, minW: 5, minH: 4 },
      config: {
        source: "attribute",
        attributeId: "",
        mode: "static",
      },
    };
  }
  if (type === "rule-graph") {
    return {
      id: crypto.randomUUID(),
      type: "rule-graph",
      title: "Rule Graph",
      layout: { x: 0, y: index * 6, w: 8, h: 6, minW: 4, minH: 4 },
      config: {
        source: "attribute",
        rootAttributeId: "",
        parentDepth: 1,
        childDepth: 2,
      },
    };
  }
  return {
    id: crypto.randomUUID(),
    type: "attribute-list",
    title: "Attributes",
    layout: { x: 0, y: index * 5, w: 6, h: 5, minW: 3, minH: 2 },
    config: {
      source: "attributes",
      attributes: [],
    },
  };
};

const getCardTitle = (card: InvestigationCardDefinition) =>
  card.title ||
  (card.type === "text"
    ? "Notes"
    : card.type === "timesheet-table"
      ? "Timesheet Summary"
      : card.type === "attribute-explanation"
        ? "Attribute Explanation"
        : card.type === "rule-graph"
          ? "Rule Graph"
          : card.type === "html"
            ? "Custom View"
            : "Attributes");

/** A decision graph's node for an attribute id or path: by path, else the attribute's first instance. */
const decisionGraphNodeId = (graph: DecisionGraph, attributeId: string) => {
  const id = graph.ref(attributeId) ?? graph.instancesOf(lastPathSegment(attributeId))[0];
  return id !== undefined && graph.isListed(id) ? id : undefined;
};

const findGraphNode = (graph: DecisionGraph | null | undefined, attributeId: string) => {
  if (!graph || !attributeId) return null;
  const id = decisionGraphNodeId(graph, attributeId);
  return id === undefined ? null : (graph.node(id) ?? null);
};

const getNodeKey = (node: any) => node?.path || node?.id || "";

const getCardSource = (card: InvestigationCardDefinition, sessionGoalSource: string, attributeSource: string) =>
  card.config?.source === sessionGoalSource ? sessionGoalSource : attributeSource;

const getAttributeListSource = (card: InvestigationCardDefinition) => getCardSource(card, "session-goal", "attributes");

const getAttributeExplanationSource = (card: InvestigationCardDefinition) =>
  getCardSource(card, "session-goal", "attribute");

const getRuleGraphSource = (card: InvestigationCardDefinition) => getCardSource(card, "session-goal", "attribute");

const getRuleGraphRootAttributeId = (card: InvestigationCardDefinition, sessionGoal?: string) =>
  getRuleGraphSource(card) === "session-goal"
    ? sessionGoal || ""
    : String(card.config?.rootAttributeId || card.config?.attributeId || "");

const getRuleGraphInstanceLabel = (nodeId: string, node: any) =>
  node?.path || node?.label || node?.description || nodeId;

const getRuleGraphFocusOptions = (graph: DecisionGraph | null | undefined, rootAttributeId: string): SelectOption[] => {
  if (!rootAttributeId || !graph) return [];
  const root = decisionGraphNodeId(graph, rootAttributeId);
  const rootNode = root === undefined ? undefined : graph.node(root);
  if (root === undefined || !rootNode) return [];
  const ids = rootNode.hidden
    ? Array.from(graph.instancesOf(graph.guid(root))).filter((id) => !graph.field(id, "hidden"))
    : [root];
  return ids.map((id) => ({ value: graph.deref(id), label: getRuleGraphInstanceLabel(graph.deref(id), graph.node(id)) }));
};

const getConfiguredAttributeId = (card: InvestigationCardDefinition, sessionGoal?: string) =>
  getAttributeExplanationSource(card) === "session-goal" ? sessionGoal || "" : String(card.config?.attributeId || "");

const getAttributeLabel = (releaseGraph: any, attributeId: string) => {
  const node = findGraphNode(releaseGraph, attributeId);
  if (!node) return attributeId;
  const entity = node.entity && node.entity !== "global" ? ` (${node.entity})` : "";
  return `${node.description || node.id || attributeId}${entity}`;
};

const getAttributeExplanationHistoryLabel = (node: any) => {
  return node?.description || node?.label || node?.id || "Attribute";
};

const getRelationshipLabel = (relationship: any) =>
  relationship?.text ||
  relationship?.textPlural ||
  relationship?.name ||
  relationship?.target ||
  relationship?.id ||
  "Relationship";

const getRelationshipOptions = (relationships: any[] = []) =>
  relationships
    .filter((relationship) => relationship?.name)
    .map((relationship) => ({
      value: relationship.name,
      label: getRelationshipLabel(relationship),
    }));

const findRelationship = (relationships: any[] = [], name?: string) =>
  relationships.find((relationship) => relationship?.name === name);

const normalizeConfigList = (value: unknown) => (Array.isArray(value) ? value : []);

const createTimesheetField = () => ({
  id: crypto.randomUUID(),
  header: "",
  attributeId: "",
});

const getDepth = (value: unknown, fallback: number) => {
  const depth = Number(value);
  if (!Number.isFinite(depth)) return fallback;
  return Math.max(0, Math.min(10, Math.floor(depth)));
};

/** The ids around a node in a session decision graph, as a mask over it (nothing copied). */
const focusedRuleGraphMask = (graph: DecisionGraph, rootNodeId: string, parentDepth: number, childDepth: number) => {
  const root = graph.ref(rootNodeId);
  if (root === undefined) return null;
  const visible = new Uint8Array(graph.idBound());
  visible[root] = 1;
  for (const id of graph.neighbourhood(root, parentDepth, "predecessors")) visible[id] = 1;
  for (const id of graph.neighbourhood(root, childDepth, "successors")) visible[id] = 1;
  return visible;
};

const InvestigationRuleGraphNode = (props: any) => (
  <RuleGraphNode
    {...props}
    includeContextMenu={false}
  />
);

const RuleGraphView = ({ rootNodeId }: { rootNodeId: string }) => {
  const { graph, visible, selectedNodeId, setSelectedNodeId } = useGraphVisualisation();

  return (
    <GraphView
      graph={graph}
      visible={visible}
      nodeComponent={InvestigationRuleGraphNode}
      onNodeClick={(nodeId, node: any) => setSelectedNodeId(node?.path || node?.id || nodeId)}
      selectedNode={selectedNodeId || rootNodeId}
      refitKey={rootNodeId}
      recalculateKey={rootNodeId}
      nodeRenderLimit={200}
      showHidden={false}
    />
  );
};

const AttributeListFront = ({
  card,
  sessionGraph,
  sessionGoal,
}: {
  card: InvestigationCardDefinition;
  sessionGraph: any;
  sessionGoal?: string;
}) => {
  const source = getAttributeListSource(card);
  const attributes =
    source === "session-goal"
      ? sessionGoal
        ? [sessionGoal]
        : []
      : Array.isArray(card.config?.attributes)
        ? card.config.attributes
        : [];

  if (source === "session-goal" && !sessionGoal) {
    return <div className="text-sm text-muted-foreground">No session goal available.</div>;
  }

  if (attributes.length === 0) {
    return <div className="text-sm text-muted-foreground">No attributes selected.</div>;
  }

  return (
    <div className="h-full min-h-0">
      <GraphDataInlineTree
        graph={sessionGraph}
        attributeIds={attributes}
        showValues
        showValidationState
        className="h-full"
      />
    </div>
  );
};

const AttributeListBack = ({
  card,
  releaseGraph,
  onChange,
}: {
  card: InvestigationCardDefinition;
  releaseGraph: any;
  onChange: (next: InvestigationCardDefinition) => void;
}) => {
  const source = getAttributeListSource(card);
  const attributes = Array.isArray(card.config?.attributes) ? card.config.attributes : [];

  const updateConfig = (patch: Record<string, any>) => {
    onChange({ ...card, config: { ...card.config, ...patch } });
  };

  const removeAttribute = (attributeId: string) => {
    updateConfig({ attributes: attributes.filter((item: string) => item !== attributeId) });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1 text-xs font-semibold">
        <span>Title</span>
        <TextInput
          value={card.title || ""}
          onValueChange={(title) => onChange({ ...card, title })}
          fullWidth
        />
      </div>
      <div className="flex flex-col gap-1 text-xs font-semibold">
        <span>Source</span>
        <Select
          options={[
            { value: "session-goal", label: "Session goal" },
            { value: "attributes", label: "Specific attributes" },
          ]}
          value={source}
          onValueChange={(nextSource) => updateConfig({ source: nextSource || "attributes" })}
          searchable={false}
        />
      </div>
      {source === "attributes" ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1 text-xs font-semibold">
            <span>Attributes</span>
            <GoalMenu
              graph={releaseGraph}
              goalId=""
              onGoalIdChange={(value) => {
                if (!value || attributes.includes(value)) return;
                updateConfig({ attributes: [...attributes, value] });
              }}
              allowEmpty
              placeholder="Add attribute"
              menuClassName="w-full"
            />
          </div>
          {attributes.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {attributes.map((attributeId: string) => (
                <Chip
                  key={attributeId}
                  size="sm"
                  variant="subtle"
                  className="max-w-full gap-1"
                >
                  <Chip.Label className="max-w-64 truncate">{getAttributeLabel(releaseGraph, attributeId)}</Chip.Label>
                  <button
                    type="button"
                    className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded hover:bg-background/70"
                    aria-label={`Remove ${getAttributeLabel(releaseGraph, attributeId)}`}
                    onClick={() => removeAttribute(attributeId)}
                  >
                    <Icon name="close" />
                  </button>
                </Chip>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const RuleGraphCardFront = ({
  card,
  sessionGraph,
  sessionGoal,
  focusNodeId,
}: {
  card: InvestigationCardDefinition;
  sessionGraph: any;
  sessionGoal?: string;
  focusNodeId?: string | null;
}) => {
  const selected = useAttributeSelected();
  const source = getRuleGraphSource(card);
  const rootAttributeId = getRuleGraphRootAttributeId(card, sessionGoal);
  const rootNodeId = focusNodeId || getRuleGraphFocusOptions(sessionGraph, rootAttributeId)[0]?.value || null;
  const parentDepth = getDepth(card.config?.parentDepth, 1);
  const childDepth = getDepth(card.config?.childDepth, 2);
  // the session graph narrowed to the nodes around the root (a mask, nothing copied)
  const focusedVisible = useMemo(
    () => (rootNodeId && sessionGraph ? focusedRuleGraphMask(sessionGraph, rootNodeId, parentDepth, childDepth) : null),
    [sessionGraph, rootNodeId, parentDepth, childDepth],
  );
  const rootNode = rootNodeId ? findGraphNode(sessionGraph, rootNodeId) : null;

  if (source === "session-goal" && !sessionGoal) {
    return <div className="text-sm text-muted-foreground">No session goal available.</div>;
  }

  if (!rootNodeId || !rootNode) {
    return (
      <div className="text-sm text-muted-foreground">
        {rootAttributeId ? "No visible instances found for this attribute." : "Choose a root attribute."}
      </div>
    );
  }

  if (!focusedVisible) {
    return <div className="text-sm text-muted-foreground">No graph nodes found for this focus.</div>;
  }

  return (
    <div className="h-full min-h-0 overflow-hidden">
      <RuleGraphProvider
        graph={sessionGraph}
        visible={focusedVisible}
        startNode={rootNode}
        debug={true}
        hasData={false}
        hasNebula={false}
        hasClassic={true}
        defaultView="classic-beta"
        onSelectedNodeChange={(node) => {
          if (node && !node.hidden) selected?.onSelectAttribute?.(node);
        }}
      >
        <RuleGraphView rootNodeId={rootNodeId} />
      </RuleGraphProvider>
    </div>
  );
};

const RuleGraphCardBack = ({
  card,
  releaseGraph,
  onChange,
}: {
  card: InvestigationCardDefinition;
  releaseGraph: any;
  onChange: (next: InvestigationCardDefinition) => void;
}) => {
  const source = getRuleGraphSource(card);

  const updateConfig = (patch: Record<string, any>) => {
    onChange({ ...card, config: { ...card.config, ...patch } });
  };

  const depthOptions = Array.from({ length: 7 }, (_, depth) => ({
    value: String(depth),
    label: String(depth),
  }));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1 text-xs font-semibold">
        <span>Title</span>
        <TextInput
          value={card.title || ""}
          onValueChange={(title) => onChange({ ...card, title })}
          fullWidth
        />
      </div>
      <div className="flex flex-col gap-1 text-xs font-semibold">
        <span>Source</span>
        <Select
          options={[
            { value: "session-goal", label: "Session goal" },
            { value: "attribute", label: "Specific attribute" },
          ]}
          value={source}
          onValueChange={(nextSource) => updateConfig({ source: nextSource || "attribute" })}
          searchable={false}
        />
      </div>
      {source === "attribute" ? (
        <div className="flex flex-col gap-1 text-xs font-semibold">
          <span>Root attribute</span>
          <GoalMenu
            graph={releaseGraph}
            goalId={card.config?.rootAttributeId || ""}
            onGoalIdChange={(rootAttributeId) => updateConfig({ rootAttributeId })}
            allowEmpty
            placeholder="Select root attribute"
            menuClassName="w-full"
          />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1 text-xs font-semibold">
          <span>Parent depth</span>
          <Select
            options={depthOptions}
            value={String(getDepth(card.config?.parentDepth, 1))}
            onValueChange={(parentDepth) => updateConfig({ parentDepth: Number(parentDepth || 0) })}
            searchable={false}
          />
        </div>
        <div className="flex flex-col gap-1 text-xs font-semibold">
          <span>Child depth</span>
          <Select
            options={depthOptions}
            value={String(getDepth(card.config?.childDepth, 2))}
            onValueChange={(childDepth) => updateConfig({ childDepth: Number(childDepth || 0) })}
            searchable={false}
          />
        </div>
      </div>
    </div>
  );
};

const TextCardFront = ({
  card,
  sessionGraph,
  releaseRelationships,
}: {
  card: InvestigationCardDefinition;
  sessionGraph: any;
  releaseRelationships: any[];
}) => {
  const body = String(card.config?.body || "");
  const renderedBody = useMemo(() => {
    if (!body) return "";
    try {
      return (
        processText(body, { entity: "global" }, {
          graph: sessionGraph,
          release: { relationships: releaseRelationships || [] },
        } as any)?.text || ""
      );
    } catch (error) {
      console.error(error);
      return body;
    }
  }, [body, releaseRelationships, sessionGraph]);

  return (
    <div className="whitespace-pre-wrap text-sm text-foreground">
      {body ? renderedBody : <span className="text-muted-foreground">No text yet.</span>}
    </div>
  );
};

const TextCardBack = ({
  card,
  releaseGraph,
  onChange,
}: {
  card: InvestigationCardDefinition;
  releaseGraph: any;
  onChange: (next: InvestigationCardDefinition) => void;
}) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-col gap-1 text-xs font-semibold">
      <span>Title</span>
      <TextInput
        value={card.title || ""}
        onValueChange={(title) => onChange({ ...card, title })}
        fullWidth
      />
    </div>
    <div className="flex flex-col gap-1 text-xs font-semibold">
      <TemplateField
        label="Body"
        name={`investigation-text-card-${card.id}-body`}
        value={String(card.config?.body || "")}
        onValueChange={(body) => onChange({ ...card, config: { ...card.config, body } })}
        graph={releaseGraph}
        attributeEntity="global"
        rows={8}
        fullWidth
      />
    </div>
  </div>
);

const HTML_CARD_MESSAGE_TYPE = "decisively:data";
const HTML_CARD_PROTOCOL_VERSION = 1;

const HtmlCardFront = ({ card, investigation, sessionGraph, releaseRelationships, sessionGoal, modelId, sessionId, sessionVersion, saved, getCardData }: {
  card: InvestigationCardDefinition;
  investigation: { id: string; name: string };
  sessionGraph: Parameters<typeof buildDecisionReportTree>[0] | null;
  releaseRelationships: unknown[];
  sessionGoal?: string;
  modelId?: string;
  sessionId?: string;
  sessionVersion?: number | null;
  saved: boolean;
  getCardData: CardDataGetter;
}) => {
  const html = String(card.config?.html || "");
  const bridgeVersion = useMemo(() => getCardBridgeVersion(html), [html]);
  // v2 cards request data on demand, so they get no v1 push and the decision report is not built for them.
  const decisionPayload = useMemo(() => bridgeVersion === 2 ? undefined : ({
    type: HTML_CARD_MESSAGE_TYPE, version: HTML_CARD_PROTOCOL_VERSION,
    payload: { investigation, sessionGoal, decisionReport: sessionGraph && sessionGoal ? buildDecisionReportTree(sessionGraph, sessionGoal) : [], releaseRelationships },
  }), [bridgeVersion, investigation, sessionGoal, sessionGraph, releaseRelationships]);
  return <div className="flex h-full flex-col">
    {!saved ? <p className="p-2 text-xs text-muted-foreground">Save the investigation to enable card storage.</p> : null}
    <div className="min-h-0 flex-1"><HtmlCardHost html={html} title={card.title || "Custom view"} modelId={modelId} investigationId={investigation.id} cardId={card.id} sessionId={sessionId} sessionVersion={sessionVersion} saved={saved} decisionPayload={decisionPayload} getData={getCardData} /></div>
  </div>;
};

const HtmlCardBack = ({
  card,
  onChange,
}: {
  card: InvestigationCardDefinition;
  onChange: (next: InvestigationCardDefinition) => void;
}) => {
  const notify = useNotification();
  const fileName = card.config?.fileName as string | undefined;
  const html = card.config?.html as string | undefined;

  const handleFileLoad = (loadedFileName: string, fileContent: string, file?: File) => {
    if (file && file.size > MAX_HTML_CARD_BYTES) {
      notify.error(`"${loadedFileName}" is too large. Custom HTML cards are limited to 1MB.`);
      return;
    }
    onChange({ ...card, config: { ...card.config, html: fileContent, fileName: loadedFileName } });
  };

  const handleDownload = () => {
    if (!html) return;
    try {
      downloadFileInBrowser(html, fileName || "custom-view.html", "text/html");
    } catch {
      notify.error("Could not download the file.");
    }
  };

  const handleClear = () => {
    onChange({ ...card, config: { ...card.config, html: "", fileName: undefined } });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 text-xs font-semibold">
        <span>Title</span>
        <TextInput
          value={card.title || ""}
          onValueChange={(title) => onChange({ ...card, title })}
          fullWidth
        />
      </div>
      <div className="flex flex-col gap-1 text-xs font-semibold">
        <span>Custom HTML file</span>
        {fileName ? (
          <div className="flex items-center gap-2">
            <span className="flex-1 truncate text-xs font-normal text-muted-foreground">{fileName}</span>
            <Button
              icon
              size="sm"
              variant="outline"
              label="Download file"
              onClick={handleDownload}
            >
              <Icon name="download" />
            </Button>
            <Button
              icon
              size="sm"
              variant="outline"
              label="Remove file"
              onClick={handleClear}
            >
              <Icon name="delete" />
            </Button>
          </div>
        ) : (
          <FileDropZone
            onLoad={handleFileLoad}
            accept=".html,text/html"
          />
        )}
      </div>
      <Alert
        variant="warning"
        icon={<Icon name="warning" />}
        title="Use at your own risk"
        description={
          <>
            We can't provide support for debugging or fixing problems in HTML you upload here. The file isn't included
            in project export or import due to size limits. To move it to another project, export the view from the
            view menu, or use Download above to keep a copy elsewhere.
          </>
        }
      />
      <div className="flex flex-col gap-3 text-xs font-normal text-muted-foreground">
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-foreground">How it works</span>
          <p>The uploaded file runs in a sandboxed frame. Decisively supplies session data when the file requests it, and access to this card's saved state. Platform credentials are never supplied.</p>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-foreground">Reading the data</span>
          <p>
            Add <code>{`<meta name="decisively-bridge" content="2">`}</code> to the file, then call{" "}
            <code>decisively.data.get(kind)</code> for each kind you need: <code>session</code>,{" "}
            <code>attributeValues</code>, <code>sessionGraph</code>, <code>contacts</code>, <code>ruleGraph</code>,{" "}
            <code>events</code> or <code>interaction</code> (by id). Only request{" "}
            <code>decisionReport</code> if you really need it, as it can be very large. The help button on the card has
            more detail.
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-foreground">Building your card</span>
          <p>
            Give the author instructions to your coding assistant to build a card that follows the contract above, or
            start from the example file, which requests session data and loads and saves settings.
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              asChild
              size="sm"
              variant="outline"
              label="Author instructions"
            >
              <a
                href={HTML_CARD_INSTRUCTIONS_URL}
                target="_blank"
                rel="noreferrer"
              >
                Author instructions
              </a>
            </Button>
            <Button
              asChild
              size="sm"
              variant="outline"
              label="Download example HTML"
            >
              <a
                href={HTML_CARD_EXAMPLE_URL}
                download="html-card-settings-example.html"
              >
                <Icon name="download" />
                Download example HTML
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AttributeExplanationFront = ({
  card,
  sessionGraph,
  selectedAttributeNode,
  selectedAttributeVersion,
  resetToken,
  history,
  historySelectionIndex,
  onChangedChange,
  onHistoryChange,
  onHistorySelectionConsumed,
  sessionGoal,
}: {
  card: InvestigationCardDefinition;
  sessionGraph: any;
  selectedAttributeNode: any;
  selectedAttributeVersion: number;
  resetToken: number;
  history: any[];
  historySelectionIndex: number | null;
  onChangedChange: (changed: boolean) => void;
  onHistoryChange: (history: any[]) => void;
  onHistorySelectionConsumed: () => void;
  sessionGoal?: string;
}) => {
  const mode = card.config?.mode === "dynamic" ? "dynamic" : "static";
  const configuredAttributeId = getConfiguredAttributeId(card, sessionGoal);
  const configuredNode = configuredAttributeId ? findGraphNode(sessionGraph, configuredAttributeId) : null;
  const focusNode = mode === "dynamic" ? selectedAttributeNode || configuredNode : configuredNode;
  const focusKey = getNodeKey(focusNode);
  const focusVersion = mode === "dynamic" ? selectedAttributeVersion : 0;
  const configuredKey = getNodeKey(configuredNode);
  const currentNode = history[history.length - 1];
  const currentKey = getNodeKey(currentNode);
  const didMountResetEffect = useRef(false);

  useEffect(() => {
    onHistoryChange(focusNode ? [focusNode] : []);
  }, [focusKey, focusNode, focusVersion, onHistoryChange]);

  useEffect(() => {
    if (!didMountResetEffect.current) {
      didMountResetEffect.current = true;
      return;
    }
    onHistoryChange(configuredNode ? [configuredNode] : []);
  }, [resetToken]);

  useEffect(() => {
    onChangedChange(Boolean(configuredKey && currentKey && currentKey !== configuredKey));
  }, [configuredKey, currentKey, onChangedChange]);

  useEffect(() => {
    if (historySelectionIndex === null) return;
    if (historySelectionIndex >= 0 && historySelectionIndex < history.length) {
      onHistoryChange(history.slice(0, historySelectionIndex + 1));
    }
    onHistorySelectionConsumed();
  }, [history, historySelectionIndex, onHistoryChange, onHistorySelectionConsumed]);

  const pushNode = (node: any) => {
    if (!node) return;
    const active = history[history.length - 1];
    if (getNodeKey(active) === getNodeKey(node)) return;
    onHistoryChange([...history, node]);
  };

  if (!focusNode || !currentNode) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        {mode === "dynamic" ? "Select an attribute in another card." : "Choose an attribute to explain."}
      </div>
    );
  }

  return (
    <AttributeExplanationDetailPanel
      graph={sessionGraph}
      node={currentNode}
      canGoBack={history.length > 1}
      onBack={() => onHistoryChange(history.slice(0, -1))}
      onNavigate={pushNode}
    />
  );
};

const investigationCardOnError = (error: Error, info: { componentStack?: string | null }) => {
  console.error("Investigation card failed to render", error, info.componentStack);
};

/**
 * Isolates a render crash to a single card's content (front OR back — wrapped separately so a
 * crash in one doesn't take the other's edit affordance down with it) rather than letting it take
 * down the rest of the investigation dashboard. Resets whenever the card's identity or config
 * changes (e.g. the user fixes/replaces a bad upload).
 */
const InvestigationCardErrorBoundary = ({
  card,
  children,
}: {
  card: InvestigationCardDefinition;
  children: React.ReactNode;
}) => {
  const Fallback = useCallback(
    () => (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
        <Icon
          name="error"
          className="size-6 text-destructive"
        />
        <span className="text-sm text-muted-foreground">
          {getCardTitle(card)} failed to render{card.type === "html" ? " — check the uploaded file" : ""}.
        </span>
      </div>
    ),
    [card],
  );

  return (
    <ErrorBoundary
      resetKeys={[card.id, card.type, card.config]}
      FallbackComponent={Fallback}
      onError={investigationCardOnError}
    >
      {children}
    </ErrorBoundary>
  );
};

const InvestigationCardRenderer = ({
  card,
  modelId,
  sessionId,
  sessionVersion,
  saved,
  investigation,
  sessionGraph,
  releaseGraph,
  releaseRelationships,
  sessionGoal,
  getCardData,
  selectedAttributeNode,
  selectedAttributeVersion,
  onClearSelectedAttribute,
  onUpdateCard,
  onRemoveCard,
}: {
  card: InvestigationCardDefinition;
  modelId?: string;
  sessionId?: string;
  sessionVersion?: number | null;
  saved: boolean;
  investigation: { id: string; name: string };
  sessionGraph: any;
  releaseGraph: any;
  releaseRelationships: any[];
  sessionGoal?: string;
  getCardData: CardDataGetter;
  selectedAttributeNode: any;
  selectedAttributeVersion: number;
  onClearSelectedAttribute: () => void;
  onUpdateCard: (cardId: string, nextCard: InvestigationCardDefinition) => void;
  onRemoveCard: (cardId: string) => void;
}) => {
  const [attributeExplanationChanged, setAttributeExplanationChanged] = useState(false);
  const [attributeExplanationResetToken, setAttributeExplanationResetToken] = useState(0);
  const [attributeExplanationHistory, setAttributeExplanationHistory] = useState<any[]>([]);
  const [attributeExplanationHistorySelectionIndex, setAttributeExplanationHistorySelectionIndex] = useState<
    number | null
  >(null);
  const ruleGraphRootAttributeId = card.type === "rule-graph" ? getRuleGraphRootAttributeId(card, sessionGoal) : "";
  const ruleGraphFocusOptions = useMemo(
    () => (card.type === "rule-graph" ? getRuleGraphFocusOptions(sessionGraph, ruleGraphRootAttributeId) : []),
    [card.type, sessionGraph, ruleGraphRootAttributeId],
  );
  const [ruleGraphFocusNodeId, setRuleGraphFocusNodeId] = useState<string | null>(null);
  const selectedRuleGraphFocusNodeId =
    ruleGraphFocusOptions.find((option) => option.value === ruleGraphFocusNodeId)?.value ||
    ruleGraphFocusOptions[0]?.value ||
    null;

  useEffect(() => {
    if (card.type !== "rule-graph") return;
    setRuleGraphFocusNodeId((current) =>
      ruleGraphFocusOptions.some((option) => option.value === current)
        ? current
        : ruleGraphFocusOptions[0]?.value || null,
    );
  }, [card.type, ruleGraphFocusOptions]);

  const resetAttributeExplanation = () => {
    onClearSelectedAttribute();
    setAttributeExplanationResetToken((value) => value + 1);
  };

  const attributeExplanationHistoryOptions = useMemo(
    () =>
      attributeExplanationHistory
        .map((node, index) => ({
          value: String(index),
          label: getAttributeExplanationHistoryLabel(node),
        }))
        .reverse(),
    [attributeExplanationHistory],
  );

  const headerActions =
    card.type === "html" ? <HtmlCardHelp /> : card.type === "attribute-explanation" ? (
      <>
        {attributeExplanationHistory.length > 1 ? (
          <Select
            className="w-96 max-w-[45vw]"
            options={attributeExplanationHistoryOptions}
            value={String(attributeExplanationHistory.length - 1)}
            searchable={false}
            clearable={false}
            onValueChange={(index) =>
              setAttributeExplanationHistorySelectionIndex(index === null || index === undefined ? null : Number(index))
            }
          />
        ) : null}
        {attributeExplanationChanged ? (
          <Button
            icon
            size="sm"
            variant="ghost"
            label="Reset"
            onClick={resetAttributeExplanation}
          >
            <Icon name="reset" />
          </Button>
        ) : null}
      </>
    ) : card.type === "rule-graph" && ruleGraphFocusOptions.length > 1 ? (
      <Select
        className="w-96 max-w-[45vw]"
        options={ruleGraphFocusOptions}
        value={selectedRuleGraphFocusNodeId || ""}
        searchable={false}
        clearable={false}
        onValueChange={(nodeId) => setRuleGraphFocusNodeId(nodeId || null)}
      />
    ) : null;

  return (
    <InvestigationCard
      className={card.type === "text" ? "investigation-card-template-editor" : undefined}
      title={getCardTitle(card)}
      resizable={false}
      headerActions={headerActions}
      front={
        <InvestigationCardErrorBoundary card={card}>
          {card.type === "html" ? (
            <HtmlCardFront
              modelId={modelId}
              sessionId={sessionId}
              sessionVersion={sessionVersion}
              saved={saved}
              card={card}
              investigation={investigation}
              sessionGraph={sessionGraph}
              releaseRelationships={releaseRelationships}
              sessionGoal={sessionGoal}
              getCardData={getCardData}
            />
          ) : card.type === "text" ? (
            <TextCardFront
              card={card}
              sessionGraph={sessionGraph}
              releaseRelationships={releaseRelationships}
            />
          ) : card.type === "timesheet-table" ? (
            <TimesheetCardFront
              card={card}
              sessionGraph={sessionGraph}
            />
          ) : card.type === "rule-graph" ? (
            <RuleGraphCardFront
              card={card}
              sessionGraph={sessionGraph}
              sessionGoal={sessionGoal}
              focusNodeId={selectedRuleGraphFocusNodeId}
            />
          ) : card.type === "attribute-explanation" ? (
            <AttributeExplanationFront
              card={card}
              sessionGraph={sessionGraph}
              sessionGoal={sessionGoal}
              selectedAttributeNode={selectedAttributeNode}
              selectedAttributeVersion={selectedAttributeVersion}
              resetToken={attributeExplanationResetToken}
              history={attributeExplanationHistory}
              historySelectionIndex={attributeExplanationHistorySelectionIndex}
              onChangedChange={setAttributeExplanationChanged}
              onHistoryChange={setAttributeExplanationHistory}
              onHistorySelectionConsumed={() => setAttributeExplanationHistorySelectionIndex(null)}
            />
          ) : (
            <AttributeListFront
              card={card}
              sessionGraph={sessionGraph}
              sessionGoal={sessionGoal}
            />
          )}
        </InvestigationCardErrorBoundary>
      }
      back={
        <InvestigationCardErrorBoundary card={card}>
          {card.type === "html" ? (
            <HtmlCardBack
              card={card}
              onChange={(next) => onUpdateCard(card.id, next)}
            />
          ) : card.type === "text" ? (
            <TextCardBack
              card={card}
              releaseGraph={releaseGraph}
              onChange={(next) => onUpdateCard(card.id, next)}
            />
          ) : card.type === "timesheet-table" ? (
            <TimesheetCardBack
              card={card}
              releaseGraph={releaseGraph}
              releaseRelationships={releaseRelationships}
              onChange={(next) => onUpdateCard(card.id, next)}
            />
          ) : card.type === "rule-graph" ? (
            <RuleGraphCardBack
              card={card}
              releaseGraph={releaseGraph}
              onChange={(next) => onUpdateCard(card.id, next)}
            />
          ) : card.type === "attribute-explanation" ? (
            <AttributeExplanationBack
              card={card}
              releaseGraph={releaseGraph}
              onChange={(next) => onUpdateCard(card.id, next)}
            />
          ) : (
            <AttributeListBack
              card={card}
              releaseGraph={releaseGraph}
              onChange={(next) => onUpdateCard(card.id, next)}
            />
          )}
        </InvestigationCardErrorBoundary>
      }
      onDelete={() => onRemoveCard(card.id)}
    />
  );
};

const AttributeExplanationBack = ({
  card,
  releaseGraph,
  onChange,
}: {
  card: InvestigationCardDefinition;
  releaseGraph: any;
  onChange: (next: InvestigationCardDefinition) => void;
}) => {
  const source = getAttributeExplanationSource(card);

  const updateConfig = (patch: Record<string, any>) => {
    onChange({ ...card, config: { ...card.config, ...patch } });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1 text-xs font-semibold">
        <span>Title</span>
        <TextInput
          value={card.title || ""}
          onValueChange={(title) => onChange({ ...card, title })}
          fullWidth
        />
      </div>
      <div className="flex flex-col gap-1 text-xs font-semibold">
        <span>Mode</span>
        <Select
          options={[
            { value: "static", label: "Static" },
            { value: "dynamic", label: "Dynamic" },
          ]}
          value={card.config?.mode === "dynamic" ? "dynamic" : "static"}
          onValueChange={(mode) => updateConfig({ mode: mode || "static" })}
          searchable={false}
        />
      </div>
      <div className="flex flex-col gap-1 text-xs font-semibold">
        <span>Source</span>
        <Select
          options={[
            { value: "session-goal", label: "Session goal" },
            { value: "attribute", label: "Specific attribute" },
          ]}
          value={source}
          onValueChange={(nextSource) => updateConfig({ source: nextSource || "attribute" })}
          searchable={false}
        />
      </div>
      {source === "attribute" ? (
        <div className="flex flex-col gap-1 text-xs font-semibold">
          <span>Focus attribute</span>
          <GoalMenu
            graph={releaseGraph}
            goalId={card.config?.attributeId || ""}
            onGoalIdChange={(attributeId) => updateConfig({ attributeId })}
            allowEmpty
            placeholder="Select attribute"
            menuClassName="w-full"
          />
        </div>
      ) : null}
    </div>
  );
};

const InvestigationMiniMap = ({
  cards,
  scrollContainerRef,
}: {
  cards: InvestigationCardDefinition[];
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [viewport, setViewport] = useState({
    scrollLeft: 0,
    scrollTop: 0,
    clientWidth: 0,
    clientHeight: 0,
    scrollWidth: 0,
    scrollHeight: 0,
  });

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateViewport = () => {
      setViewport({
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
        clientWidth: container.clientWidth,
        clientHeight: container.clientHeight,
        scrollWidth: container.scrollWidth,
        scrollHeight: container.scrollHeight,
      });
    };

    updateViewport();
    container.addEventListener("scroll", updateViewport, { passive: true });
    const resizeObserver = new ResizeObserver(updateViewport);
    resizeObserver.observe(container);
    for (const child of Array.from(container.children)) {
      resizeObserver.observe(child);
    }

    return () => {
      container.removeEventListener("scroll", updateViewport);
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setViewport({
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
      clientWidth: container.clientWidth,
      clientHeight: container.clientHeight,
      scrollWidth: container.scrollWidth,
      scrollHeight: container.scrollHeight,
    });
  }, [cards, scrollContainerRef]);

  if (cards.length === 0) return null;

  const contentWidth = Math.max(viewport.scrollWidth, viewport.clientWidth);
  const contentHeight = Math.max(viewport.scrollHeight, viewport.clientHeight);
  const hasOverflow = contentWidth > viewport.clientWidth + 1 || contentHeight > viewport.clientHeight + 1;
  if (!hasOverflow) return null;

  const scale = Math.min(MINIMAP_WIDTH / contentWidth, MINIMAP_HEIGHT / contentHeight);
  const mapWidth = Math.max(1, contentWidth * scale);
  const mapHeight = Math.max(1, contentHeight * scale);
  const columnWidth = contentWidth / GRID_COLUMNS;
  const [marginX, marginY] = GRID_MARGIN;
  const cardRects = cards.map((card) => {
    const layout = card.layout;
    return {
      id: card.id,
      label: getCardTypeInitials(card.type),
      x: layout.x * columnWidth * scale,
      y: layout.y * (ROW_HEIGHT + marginY) * scale,
      width: Math.max(8, (layout.w * columnWidth - marginX) * scale),
      height: Math.max(8, (layout.h * ROW_HEIGHT + Math.max(0, layout.h - 1) * marginY) * scale),
    };
  });
  const viewportRect = {
    x: viewport.scrollLeft * scale,
    y: viewport.scrollTop * scale,
    width: Math.max(12, viewport.clientWidth * scale),
    height: Math.max(12, viewport.clientHeight * scale),
  };

  const moveViewport = (event: React.PointerEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const nextLeft = (event.clientX - bounds.left) / scale - container.clientWidth / 2;
    const nextTop = (event.clientY - bounds.top) / scale - container.clientHeight / 2;
    container.scrollTo({
      left: Math.max(0, Math.min(nextLeft, container.scrollWidth - container.clientWidth)),
      top: Math.max(0, Math.min(nextTop, container.scrollHeight - container.clientHeight)),
      behavior: "auto",
    });
  };

  if (collapsed) {
    return (
      <div className="pointer-events-auto sticky bottom-3 ml-auto mr-3 mt-[-44px] flex w-fit justify-end">
        <Button
          icon
          size="sm"
          variant="outline"
          label="Expand minimap"
          className="bg-background/95 shadow-md backdrop-blur"
          onClick={() => setCollapsed(false)}
        >
          <Icon name="map" />
        </Button>
      </div>
    );
  }

  return (
    <div className="pointer-events-auto sticky bottom-3 ml-auto mr-3 mt-[-132px] flex w-fit justify-end">
      <div className="rounded border border-border bg-background/95 p-2 shadow-md backdrop-blur">
        <div className="mb-1 flex items-center justify-end">
          <Button
            icon
            size="sm"
            variant="ghost"
            label="Collapse minimap"
            onClick={() => setCollapsed(true)}
          >
            <Icon name="collapse" />
          </Button>
        </div>
        <div
          className="relative cursor-pointer overflow-hidden rounded bg-muted/50"
          style={{ width: mapWidth, height: mapHeight }}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            moveViewport(event);
          }}
          onPointerMove={(event) => {
            if (event.buttons !== 1) return;
            moveViewport(event);
          }}
        >
          {cardRects.map((rect) => (
            <div
              key={rect.id}
              className="absolute flex items-center justify-center rounded-sm border border-slate-500 bg-slate-100 text-[9px] font-bold leading-none text-slate-950 shadow-sm"
              style={{
                left: rect.x,
                top: rect.y,
                width: rect.width,
                height: rect.height,
              }}
            >
              {rect.width >= 16 && rect.height >= 12 ? rect.label : null}
            </div>
          ))}
          <div
            className="absolute rounded-sm border-2 border-primary bg-primary/10"
            style={{
              left: viewportRect.x,
              top: viewportRect.y,
              width: viewportRect.width,
              height: viewportRect.height,
            }}
          />
        </div>
      </div>
    </div>
  );
};

const TimesheetCardFront = ({ card, sessionGraph }: { card: InvestigationCardDefinition; sessionGraph: any }) => (
  <TimesheetInvestigationCard
    graph={sessionGraph}
    config={card.config}
  />
);

const TimesheetConfiguratorRows = ({
  label,
  items,
  releaseGraph,
  entityName,
  onChange,
}: {
  label: string;
  items: any[];
  releaseGraph: any;
  entityName?: string;
  onChange: (nextItems: any[]) => void;
}) => {
  const updateItem = (index: number, patch: Record<string, any>) => {
    onChange(items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)));
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold">{label}</span>
        <Button
          size="sm"
          variant="outline"
          label={`Add ${label.toLowerCase()}`}
          onClick={() => onChange([...items, createTimesheetField()])}
        >
          <Icon name="add" />
          Add
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-muted-foreground">No {label.toLowerCase()} configured.</div>
      ) : null}
      {items.map((item, index) => (
        <div
          key={item.id || index}
          className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] items-end gap-2 rounded border border-border p-2"
        >
          <div className="flex flex-col gap-1 text-xs font-semibold">
            <span>Header</span>
            <TextInput
              value={item.header || ""}
              onValueChange={(header) => updateItem(index, { header })}
              fullWidth
            />
          </div>
          <div className="flex flex-col gap-1 text-xs font-semibold">
            <span>Attribute</span>
            <GoalMenu
              graph={releaseGraph}
              goalId={item.attributeId || ""}
              onGoalIdChange={(attributeId) => updateItem(index, { attributeId })}
              allowEmpty
              placeholder="Select attribute"
              menuClassName="w-full"
              entityName={entityName}
            />
          </div>
          <Button
            icon
            size="sm"
            variant="ghost"
            intent="destructive"
            label={`Remove ${label.toLowerCase()}`}
            onClick={() => removeItem(index)}
          >
            <Icon name="delete" />
          </Button>
        </div>
      ))}
    </div>
  );
};

const TimesheetCardBack = ({
  card,
  releaseGraph,
  releaseRelationships,
  onChange,
}: {
  card: InvestigationCardDefinition;
  releaseGraph: any;
  releaseRelationships: any[];
  onChange: (next: InvestigationCardDefinition) => void;
}) => {
  const relationshipOptions = useMemo(() => getRelationshipOptions(releaseRelationships), [releaseRelationships]);
  const columns = normalizeConfigList(card.config?.columns);
  const totals = normalizeConfigList(card.config?.totals);
  const selectedPositionRelationship = findRelationship(releaseRelationships, card.config?.positionRelationship);
  const selectedTimeEntryRelationship = findRelationship(releaseRelationships, card.config?.timeEntryRelationship);
  const positionEntity = selectedPositionRelationship?.name || selectedPositionRelationship?.target;
  const timeEntryEntity = selectedTimeEntryRelationship?.name || selectedTimeEntryRelationship?.target;

  const updateConfig = (patch: Record<string, any>) => {
    onChange({ ...card, config: { ...card.config, ...patch } });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1 text-xs font-semibold">
        <span>Title</span>
        <TextInput
          value={card.title || ""}
          onValueChange={(title) => onChange({ ...card, title })}
          fullWidth
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1 text-xs font-semibold">
          <span>Position object</span>
          <Select
            options={relationshipOptions}
            value={card.config?.positionRelationship || null}
            onValueChange={(positionRelationship) => updateConfig({ positionRelationship: positionRelationship || "" })}
            placeholder="Select relationship"
          />
        </div>
        <div className="flex flex-col gap-1 text-xs font-semibold">
          <span>Time entry object</span>
          <Select
            options={relationshipOptions}
            value={card.config?.timeEntryRelationship || null}
            onValueChange={(timeEntryRelationship) =>
              updateConfig({ timeEntryRelationship: timeEntryRelationship || "" })
            }
            placeholder="Select relationship"
          />
        </div>
      </div>
      <TimesheetConfiguratorRows
        label="Columns"
        items={columns}
        releaseGraph={releaseGraph}
        entityName={timeEntryEntity}
        onChange={(nextColumns) => updateConfig({ columns: nextColumns })}
      />
      <TimesheetConfiguratorRows
        label="Totals"
        items={totals}
        releaseGraph={releaseGraph}
        entityName={positionEntity}
        onChange={(nextTotals) => updateConfig({ totals: nextTotals })}
      />
    </div>
  );
};

export const InvestigationsTab = ({
  modelId,
  sessionId,
  sessionVersion,
  session,
  sessionGraph,
  releaseGraph,
  ruleGraph,
  releaseRelationships,
  sessionGoal,
  canReadContacts = false,
}: {
  modelId?: string;
  sessionId?: string;
  sessionVersion?: number | null;
  /** The viewed session record, served to HTML cards on request (data bridge v2). */
  session?: Record<string, unknown> | null;
  sessionGraph: any;
  releaseGraph: any;
  /** The release rule graph in its stored form, served to HTML cards on request (data bridge v2). */
  ruleGraph?: unknown;
  releaseRelationships?: any[];
  sessionGoal?: string;
  canReadContacts?: boolean;
}) => {
  const notify = useNotification();
  const getCardData = useCardDataSource({
    session,
    sessionGraph,
    ruleGraph,
    sessionGoal,
    canReadContacts,
    loadContacts: fetchSessionContactRecords,
    loadEvents: fetchAllSessionEvents,
    loadInteraction: fetchSessionInteraction,
  });
  const [items, setItems] = useState<InvestigationMetadata[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [record, setRecord] = useState<InvestigationRecord | null>(null);
  const [template, setTemplate] = useState<InvestigationTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selectedAttributeSelection, setSelectedAttributeSelection] = useState<{
    node: any;
    version: number;
  } | null>(null);
  const notifyRef = useRef(notify);
  const dashboardScrollRef = useRef<HTMLDivElement | null>(null);
  const isDefaultInvestigation = Boolean(record?.isDefault || record?.id === "default");

  useEffect(() => {
    notifyRef.current = notify;
  }, [notify]);

  const refreshList = useCallback(
    async (force = false) => {
      if (!modelId) return;
      const nextItems = await fetchInvestigationList(modelId, force);
      setItems(nextItems);
      setSelectedId((current) => current ?? nextItems[0]?.id ?? null);
    },
    [modelId],
  );

  const loadSelected = useCallback(
    async (investigationId: string) => {
      if (!modelId) return;
      setLoading(true);
      try {
        const nextRecord = await fetchInvestigationRecord(modelId, investigationId);
        setRecord(nextRecord);
        setTemplate(nextRecord.template);
        setDirty(false);
      } catch (error) {
        console.error(error);
        notifyRef.current.error("Failed to load investigation");
      } finally {
        setLoading(false);
      }
    },
    [modelId],
  );

  useEffect(() => {
    refreshList().catch((error) => {
      console.error(error);
      notifyRef.current.error("Failed to load investigations");
    });
  }, [refreshList]);

  useEffect(() => {
    if (selectedId) {
      loadSelected(selectedId);
    } else {
      setRecord(null);
      setTemplate(null);
    }
  }, [selectedId, loadSelected]);

  const mutateTemplate = (fn: (current: InvestigationTemplate) => InvestigationTemplate) => {
    setTemplate((current) => {
      if (!current) return current;
      const next = fn(current);
      setDirty(true);
      return next;
    });
  };

  const createNew = async () => {
    if (!modelId) return;
    const name = `Investigation ${items.length + 1}`;
    setSaving(true);
    try {
      const response = await createInvestigation.submit({
        params: { id: modelId },
        body: {
          name,
          template: defaultTemplate(modelId, "", name),
        },
      } as any);
      setCachedInvestigationValue(
        investigationRecordCache,
        getInvestigationRecordCacheKey(modelId, response.data.id),
        response.data,
      );
      await refreshList(true);
      setSelectedId(response.data.id);
      setRecord(response.data);
      setTemplate(response.data.template);
      setDirty(false);
    } catch (error) {
      console.error(error);
      notify.error("Failed to create investigation");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!modelId || !record || !template) return;
    setSaving(true);
    try {
      const response = await updateInvestigation.submit({
        params: { id: modelId, investigationId: record.id },
        body: {
          name: isDefaultInvestigation ? "Decision" : template.name || record.name,
          description: record.description || "",
          template: isDefaultInvestigation ? { ...template, name: "Decision" } : template,
        },
      } as any);
      setCachedInvestigationValue(
        investigationRecordCache,
        getInvestigationRecordCacheKey(modelId, response.data.id),
        response.data,
      );
      setRecord(response.data);
      setTemplate(response.data.template);
      setDirty(false);
      await refreshList(true);
      notify.success("Investigation saved");
    } catch (error) {
      console.error(error);
      notify.error("Failed to save investigation");
    } finally {
      setSaving(false);
    }
  };

  const removeInvestigation = async () => {
    if (!modelId || !record) return false;
    if (isDefaultInvestigation) return false;
    setSaving(true);
    try {
      await deleteInvestigation.submit({ params: { id: modelId, investigationId: record.id } });
      investigationRecordCache.delete(getInvestigationRecordCacheKey(modelId, record.id));
      setSelectedId(null);
      setRecord(null);
      setTemplate(null);
      await refreshList(true);
      notify.success("Investigation deleted");
      return true;
    } catch (error) {
      console.error(error);
      notify.error("Failed to delete investigation");
      return false;
    } finally {
      setSaving(false);
    }
  };

  /** Saves only the new name, so unsaved card edits stay as a draft. */
  const renameInvestigation = async (name: string) => {
    if (!modelId || !record || isDefaultInvestigation) return false;
    try {
      const response = await updateInvestigation.submit({
        params: { id: modelId, investigationId: record.id },
        body: { name, description: record.description || "" },
      } as any);
      setCachedInvestigationValue(
        investigationRecordCache,
        getInvestigationRecordCacheKey(modelId, response.data.id),
        response.data,
      );
      setRecord(response.data);
      setTemplate((current) => (current ? { ...current, name: response.data.name } : current));
      await refreshList(true);
      notify.success("Investigation renamed");
      return true;
    } catch (error) {
      console.error(error);
      notify.error("Failed to rename investigation");
      return false;
    }
  };

  const updateCard = (cardId: string, nextCard: InvestigationCardDefinition) => {
    mutateTemplate((current) => ({
      ...current,
      canvas: {
        ...current.canvas,
        cards: current.canvas.cards.map((card) => (card.id === cardId ? nextCard : card)),
      },
    }));
  };

  const addCard = (type: InvestigationCardType) => {
    mutateTemplate((current) => ({
      ...current,
      canvas: {
        ...current.canvas,
        cards: [...current.canvas.cards, createCard(type, current.canvas.cards.length)],
      },
    }));
  };

  const removeCard = (cardId: string) => {
    mutateTemplate((current) => ({
      ...current,
      canvas: {
        ...current.canvas,
        cards: current.canvas.cards.filter((card) => card.id !== cardId),
      },
    }));
  };

  const selectAttribute = useCallback((node: any) => {
    setSelectedAttributeSelection((current) => ({
      node,
      version: (current?.version || 0) + 1,
    }));
  }, []);

  const clearSelectedAttribute = useCallback(() => {
    setSelectedAttributeSelection(null);
  }, []);

  const gridLayout = useMemo(
    () =>
      (template?.canvas.cards || []).map((card) => ({
        i: card.id,
        ...card.layout,
      })),
    [template?.canvas.cards],
  );

  const handleGridLayoutChange = useCallback((nextLayout: Layout[]) => {
    setTemplate((current) => {
      if (!current) return current;
      const layoutById = new Map(nextLayout.map((item) => [item.i, item]));
      let changed = false;
      const cards = current.canvas.cards.map((card) => {
        const nextItem = layoutById.get(card.id);
        if (!nextItem) return card;
        const nextCardLayout = toStoredGridLayout(nextItem);
        if (isSameLayout(card.layout, nextCardLayout)) {
          return card;
        }
        changed = true;
        return { ...card, layout: nextCardLayout };
      });

      if (!changed) return current;
      setDirty(true);
      return {
        ...current,
        canvas: {
          ...current.canvas,
          cards,
        },
      };
    });
  }, []);

  const handleImported = async (result: InvestigationImportResult) => {
    if (!modelId) return;
    for (const item of result.investigations) {
      investigationRecordCache.delete(getInvestigationRecordCacheKey(modelId, item.id));
    }
    await refreshList(true);
    // Show the first new page; if the import only replaced the Decision page, show that instead.
    const target = result.investigations.find((item) => !item.isDefault) ?? result.investigations[0];
    if (!target) return;
    if (target.id === selectedId) {
      await loadSelected(target.id);
    } else {
      setSelectedId(target.id);
    }
  };

  const changeSelectedInvestigation = (investigationId: string) => {
    if (investigationId === selectedId) return;
    setSelectedId(investigationId);
  };

  const currentInvestigationName = isDefaultInvestigation
    ? "Decision"
    : template?.name || items.find((item) => item.id === selectedId)?.name || "Investigation";

  if (!modelId) {
    return <div className="p-6 text-sm text-muted-foreground">No model selected.</div>;
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-muted/20">
      <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-border bg-background px-4">
        <div className="min-w-0">
          <InvestigationViewSelector
            modelId={modelId}
            items={items}
            selectedId={selectedId}
            currentName={currentInvestigationName}
            dirty={dirty}
            creating={saving}
            onSelect={changeSelectedInvestigation}
            onCreate={createNew}
            onRename={renameInvestigation}
            onDelete={removeInvestigation}
            onImported={handleImported}
          />
        </div>
        {template ? (
          <div className="flex shrink-0 items-center gap-2">
            {dirty ? (
              <span className="mr-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <span
                  className="size-1.5 rounded-full bg-warning"
                  aria-hidden="true"
                />
                Unsaved changes
              </span>
            ) : null}
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  label="Add Card"
                >
                  <Icon name="add" />
                  Add Card
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content
                align="end"
                className="min-w-44"
              >
                <DropdownMenu.Item onSelect={() => addCard("attribute-list")}>
                  <DropdownMenu.Item.Icon>
                    <Icon name="show-data" />
                  </DropdownMenu.Item.Icon>
                  <DropdownMenu.Item.Label>Attribute list</DropdownMenu.Item.Label>
                </DropdownMenu.Item>
                <DropdownMenu.Item onSelect={() => addCard("timesheet-table")}>
                  <DropdownMenu.Item.Icon>
                    <Icon name="calendar-time" />
                  </DropdownMenu.Item.Icon>
                  <DropdownMenu.Item.Label>Timesheet table</DropdownMenu.Item.Label>
                </DropdownMenu.Item>
                <DropdownMenu.Item onSelect={() => addCard("attribute-explanation")}>
                  <DropdownMenu.Item.Icon>
                    <Icon name="info" />
                  </DropdownMenu.Item.Icon>
                  <DropdownMenu.Item.Label>Attribute explanation</DropdownMenu.Item.Label>
                </DropdownMenu.Item>
                <DropdownMenu.Item onSelect={() => addCard("rule-graph")}>
                  <DropdownMenu.Item.Icon>
                    <Icon name="build" />
                  </DropdownMenu.Item.Icon>
                  <DropdownMenu.Item.Label>Rule graph</DropdownMenu.Item.Label>
                </DropdownMenu.Item>
                <DropdownMenu.Item onSelect={() => addCard("text")}>
                  <DropdownMenu.Item.Icon>
                    <Icon name="text" />
                  </DropdownMenu.Item.Icon>
                  <DropdownMenu.Item.Label>Text</DropdownMenu.Item.Label>
                </DropdownMenu.Item>
                <DropdownMenu.Item onSelect={() => addCard("html")}>
                  <DropdownMenu.Item.Icon>
                    <Icon name="code" />
                  </DropdownMenu.Item.Icon>
                  <DropdownMenu.Item.Label>Custom HTML</DropdownMenu.Item.Label>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
            <Button
              size="sm"
              label="Save investigation"
              onClick={save}
              loading={saving}
              disabled={!dirty && !saving}
            >
              Save
            </Button>
          </div>
        ) : null}
      </header>
      <div
        ref={dashboardScrollRef}
        className="min-h-0 flex-1 overflow-auto p-1"
      >
        {loading ? <div className="text-sm text-muted-foreground">Loading investigation...</div> : null}
        {!loading && template ? (
          <div className="relative min-h-[520px] min-w-[960px] bg-background p-1">
            {template.canvas.cards.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                Add a card to start this investigation.
              </div>
            ) : null}
            <AttributeSelectedProvider onSelectAttribute={selectAttribute}>
              <DashboardGridLayout
                layout={gridLayout}
                cols={GRID_COLUMNS}
                rowHeight={ROW_HEIGHT}
                margin={GRID_MARGIN}
                containerPadding={[0, 0]}
                draggableHandle=".investigation-card-drag-handle"
                compactType={null}
                preventCollision
                resizeHandles={["se"]}
                onLayoutChange={handleGridLayoutChange}
              >
                {template.canvas.cards.map((card) => (
                  <div key={card.id}>
                    <InvestigationCardRenderer
                      modelId={modelId}
                      sessionId={sessionId}
                      sessionVersion={sessionVersion}
                      saved={Boolean(record?.template.canvas.cards.some(savedCard => savedCard.id === card.id && savedCard.type === "html" && savedCard.config?.html === card.config?.html))}
                      card={card}
                      investigation={{ id: template.id, name: template.name }}
                      sessionGraph={sessionGraph}
                      releaseGraph={releaseGraph}
                      releaseRelationships={releaseRelationships || []}
                      sessionGoal={sessionGoal}
                      getCardData={getCardData}
                      selectedAttributeNode={selectedAttributeSelection?.node ?? null}
                      selectedAttributeVersion={selectedAttributeSelection?.version ?? 0}
                      onClearSelectedAttribute={clearSelectedAttribute}
                      onUpdateCard={updateCard}
                      onRemoveCard={removeCard}
                    />
                  </div>
                ))}
              </DashboardGridLayout>
            </AttributeSelectedProvider>
            <InvestigationMiniMap
              cards={template.canvas.cards}
              scrollContainerRef={dashboardScrollRef}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default InvestigationsTab;
